export default class ArticleNavigation {
  constructor(options = {}) {
    this.settings = {
      speed: 1000,
      offset: this.header?.offsetHeight || 0,
      currentClass: "js-anchor-active",
      updateHash: true,
      classToParent: true,
      ...options,
    };
    this.header = document.querySelector(".js-header");
    this.navbar = document.querySelector(".contents-menu");
    this.rootBlock = document
      .querySelector(".contents-menu")
      .closest(".js-sidebar-nav");
    this.items = Array.from(this.navbar.querySelectorAll("a:not(.externals)"));
    this.sections = []; // DOM elements
    this.sectionMeta = []; // cached positions
    this.hashNow = undefined;
    this.lastItemFlag = false;
    this.rafRunning = false;
    this.isClickScrolling = false;
    this.isDesktopActive = false;

    this.boundOnScrollRaf = this.onScrollRaf.bind(this);
    this.boundDebouncedResize = this.debounce(() => this.handleResize(), 120);

    this.collectSections();
    this.bindBaseEvents();
  }

  bindBaseEvents() {
    // Always active on click
    this.items.forEach((item) =>
      item.addEventListener("click", (e) => this.handleClick(e))
    );
  }

  initDesktopListeners() {
    if (this.isDesktopActive) return;

    window.addEventListener("scroll", this.boundOnScrollRaf);
    window.addEventListener("resize", this.boundDebouncedResize);

    this.calcSectionMeta();
    this.scrollPage();
    this.isDesktopActive = true;
  }

  onScrollRaf() {
    if (!this.isDesktopActive || this.rafRunning) return;

    this.rafRunning = true;
    requestAnimationFrame(() => {
      this.scrollPage();
      this.rafRunning = false;
    });
  }

  // ------------------------------
  // Collect and Cache
  // ------------------------------
  collectSections() {
    this.sections = this.items
      .map((item) =>
        document.getElementById(item.getAttribute("href").replace("#", ""))
      )
      .filter(Boolean);
  }

  calcSectionMeta() {
    this.sectionMeta = this.sections.map((sec) => ({
      id: sec.id,
      top: sec.offsetTop,
      bottom: sec.offsetTop + sec.offsetHeight,
    }));
  }

  handleResize() {
    this.settings.offset = this.header?.offsetHeight || 0;
    this.calcSectionMeta();
    this.scrollPage();
  }

  // ------------------------------
  // Smooth Scroll on Click
  // ------------------------------
  handleClick(e) {
    e.preventDefault();
    const hash = e.currentTarget.getAttribute("href");
    const id = hash.replace("#", "");

    // Escape ID for querySelector
    const escapedHash = `#${CSS.escape(id)}`;
    const target = document.querySelector(escapedHash);

    if (!target) return;

    // Determine the final position
    const rect = target.getBoundingClientRect();
    const absoluteTop = rect.top + window.pageYOffset;
    const position = absoluteTop - this.settings.offset - 90;

    this.isClickScrolling = true;

    window.scrollTo({
      top: position,
      behavior: "smooth",
    });

    // Update URL hash
    if (this.settings.updateHash) {
      history.pushState({}, "", hash);
      this.hashNow = hash;
    }

    // Reset the click scrolling flag after animation
    const scrollTimeout = this.settings.speed + 50;
    setTimeout(() => {
      this.isClickScrolling = false;
      this.scrollPage(); // Ensure correct state after scrolling
    }, scrollTimeout);
  }

  // ------------------------------
  // Scroll Listener
  // ------------------------------
  scrollPage() {
    if (!this.isDesktopActive || this.isClickScrolling) return;

    const winTop = window.scrollY;
    const winBot = winTop + window.innerHeight;

    let activeId = undefined;

    for (let meta of this.sectionMeta) {
      if (winTop <= meta.top && meta.bottom < winBot) {
        activeId = meta.id;
        break;
      }
    }

    if (!activeId) return;

    // Update URL hash
    if (this.settings.updateHash) {
      const hash = `#${activeId}`;
      if (this.hashNow !== hash) {
        this.hashNow = hash;
        history.pushState({}, "", hash);
      }
    }

    // Apply active state
    if (this.settings.classToParent) {
      this.applyClassToParent(activeId);
    } else {
      this.applySimpleClass(activeId);
    }
  }

  // ------------------------------
  // Scroll sidebar container only (without affecting main window)
  // ------------------------------
  scrollSidebarToItem(el) {
    if (!el || !this.rootBlock) return;

    const container = this.rootBlock;
    const containerRect = container.getBoundingClientRect();
    const elRect = el.getBoundingClientRect();

    if (elRect.top < containerRect.top) {
      container.scrollTop -= containerRect.top - elRect.top;
    } else if (elRect.bottom > containerRect.bottom) {
      container.scrollTop += elRect.bottom - containerRect.bottom;
    }
  }

  // ------------------------------
  // MODE 1: Add Class Only to Link
  // ------------------------------
  applySimpleClass(id) {
    this.items.forEach((i) => i.classList.remove(this.settings.currentClass));
    const active = this.navbar.querySelector(`a[href="#${id}"]`);

    if (active) {
      active.classList.add(this.settings.currentClass);
      this.scrollSidebarToItem(active);
    }
  }

  // ------------------------------
  // MODE 2: Add Class to Parent Nodes (sidebar mode)
  // ------------------------------
  applyClassToParent(id) {
    this.items.forEach((a) =>
      a.parentElement.classList.remove(this.settings.currentClass)
    );

    const link = this.navbar.querySelector(`a[href="#${id}"]`);
    if (!link) return;

    const parent = link.parentElement;
    const parentLi = link.closest(".contents-menu > li");

    parent.classList.add(this.settings.currentClass);
    parentLi?.classList.add(this.settings.currentClass);

    const isFirst = this.sections[0].id === id;
    const isLast = this.sections[this.sections.length - 1]?.id === id;

    // auto-scroll in sidebar
    if (parent && (this.isClickScrolling || (!isFirst && !isLast))) {
      this.scrollSidebarToItem(parent);
    }
  }

  // ------------------------------
  // Debounce helper
  // ------------------------------
  debounce(fn, ms) {
    let timer;
    return (...args) => {
      clearTimeout(timer);
      timer = setTimeout(() => fn.apply(this, args), ms);
    };
  }

  // ------------------------------
  // Destroy method
  // ------------------------------
  destroyDesktopListeners() {
    if (!this.isDesktopActive) return;

    window.removeEventListener("scroll", this.boundOnScrollRaf);
    window.removeEventListener("resize", this.boundDebouncedResize);

    this.items.forEach((a) => {
      a.parentElement.classList.remove(this.settings.currentClass);
      a.classList.remove(this.settings.currentClass);
    });

    this.isDesktopActive = false;
  }
}
